import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import ImageHandler from './ImageHandler';
import AudioHandler from './AudioHandler';
import Input from '../../Input';
import produce from 'immer';
import { ReactComponent as Delete } from '../../../assets/svg/delete.svg';
import { ReactComponent as Edit } from '../../../assets/svg/edit.svg';
import { ReactComponent as Add } from '../../../assets/svg/add.svg';
import { useImages } from '../../../hooks/useImages';
import { useAudios } from '../../../hooks/useAudios';
import Modal from '../../Modal';
import Button from '../../Button';
import clean from 'clean-deep';

const LessonEditDataUnionWithBox = ({ data = [], onChange = () => {} }) => {
  const [state, setState] = useState({});

  const [extractedAudios, setExtractedAudios] = useState([]);
  const [extractedImages, setExtractedImages] = useState([]);

  const images = useImages(extractedImages);
  const audios = useAudios(extractedAudios);

  const [showItemModal, setShowItemModal] = useState(undefined);

  const [itemText1, setItemText1] = useState('');
  const [itemText2, setItemText2] = useState('');

  const [imageBase, setImageBase] = useState('');
  const [audioBase, setAudioBase] = useState('');

  useEffect(() => {
    const state = formatState(data);
    setState(state);
    setExtractedImages([state.image]);
    setExtractedAudios([state.audio]);
    setImageBase(state.image);
    setAudioBase(state.audio);
  }, [data]);

  useEffect(() => {
    onChange({
      data: clean(
        translateState({ ...state, image: imageBase, audio: audioBase })
      ),
    });
  }, [imageBase, audioBase, state, onChange]);

  const onDeleteItem = (index) =>
    setState(
      produce((state) => {
        state.alternatives.splice(index, 1);
      })
    );

  const onAddItem = () => {
    setItemText1('');
    setItemText2('');

    setShowItemModal({
      submit: 'Agregar',
      onSubmit: ({ text1, text2 }) => {
        setState(
          produce((state) => {
            state.alternatives.push({
              text1: (text1 || '').trim(),
              text2: (text2 || '').trim(),
            });
          })
        );
        setShowItemModal(undefined);
      },
    });
  };

  const onEditItem = (index) => {
    setItemText1(state.alternatives[index].text1);
    setItemText2(state.alternatives[index].text2);

    setShowItemModal({
      submit: 'Guardar',
      onSubmit: ({ text1, text2 }) => {
        setState(
          produce((state) => {
            state.alternatives[index].text1 = (text1 || '').trim();
            state.alternatives[index].text2 = (text2 || '').trim();
          })
        );
        setShowItemModal(undefined);
      },
    });
  };

  const onChangeImage = useCallback((obj) => {
    setImageBase(obj.name);
    setExtractedImages((state) => [...state, obj.name]);
  }, []);

  const onChangeAudio = useCallback((obj) => {
    setAudioBase(obj.name);
    setExtractedAudios((state) => [...state, obj.name]);
  }, []);

  return (
    <Container>
      {showItemModal && (
        <ModalStyled>
          <ModalContainer>
            <ModalBody>
              <Sector>
                <Input
                  placeholder='Texto 1'
                  value={itemText1}
                  onChange={(e) => setItemText1(e.target.value)}
                />
              </Sector>
              <Sector>
                <Input
                  placeholder='Texto 2'
                  value={itemText2}
                  onChange={(e) => setItemText2(e.target.value)}
                />
              </Sector>
            </ModalBody>
            <ModalButtons>
              <Button
                secondary
                text='Cancelar'
                onClick={() => setShowItemModal(undefined)}
              />
              <Button
                primary
                text={showItemModal.submit}
                onClick={() =>
                  showItemModal.onSubmit({
                    text1: itemText1,
                    text2: itemText2,
                  })
                }
              />
            </ModalButtons>
          </ModalContainer>
        </ModalStyled>
      )}
      <Handlers>
        <ImageHandler image={imageBase} onChange={onChangeImage} />
        <AudioHandler audio={audioBase} onChange={onChangeAudio} />
      </Handlers>
      {(state.alternatives || []).map((item, index) => (
        <Wrapper key={index}>
          <Detail>
            <Sector>{item.text1 && <Text>{item.text1}</Text>}</Sector>-
            <Sector>{item.text2 && <Text>{item.text2}</Text>}</Sector>
          </Detail>
          <Options>
            <OptionIcon
              as={Delete}
              color='#c62828'
              onClick={() => onDeleteItem(index)}
            />
            <OptionIcon
              as={Edit}
              color='#1976d2'
              onClick={() => onEditItem(index)}
            />
          </Options>
        </Wrapper>
      ))}
      <WrapperAdd onClick={onAddItem}>
        <OptionIcon as={Add} color='#2196f3' />
      </WrapperAdd>
    </Container>
  );
};

const formatState = (state) => {
  const alternatives = state.alternatives?.[0] || [];
  let res = [];
  alternatives.forEach((item, index) => {
    res.push({
      text1: item.text,
      text2: state.alternatives?.[1]?.[index].text,
    });
  });
  return { ...state, alternatives: res || [] };
};

const translateState = (state) => {
  let pairs1 = [];
  let pairs2 = [];

  (state.alternatives || []).forEach((item) => {
    pairs1.push({
      text: item.text1,
      pair: pairs1.length,
    });

    pairs2.push({
      text: item.text2,
      pair: pairs2.length,
    });
  });

  return { ...state, alternatives: [pairs1, pairs2] };
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  padding: 15px 15px;
  border-radius: 10px;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  user-select: none;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  background-color: #caedff;
  margin-bottom: 10px;
`;

const WrapperAdd = styled.div`
  border-radius: 10px;
  border: 2px dashed #2196f3;
  min-height: 50px;
  width: 100%;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
`;

const Detail = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  & > * {
    margin: 5px;
  }
`;

const Sector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  & > * {
    margin: 5px;
  }
`;

const Handlers = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  & > * {
    margin: 5px;
  }
  margin-bottom: 20px;
`;

// const ImageTiny = styled.img`
//   width: 50px;
//   height: 50px;
//   min-width: 50px;
//   min-height: 50px;
//   border-radius: 10px;
//   background-color: black;
//   object-fit: contain;
// `;

const Text = styled.div`
  font-size: 0.9em;
`;

const Options = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionIcon = styled.div`
  padding: 5px;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  cursor: pointer;
  fill: ${(props) => props.color || 'black'};
  :not(:last-child) {
    margin-right: 10px;
  }
`;

// const Spinner = styled.img`
//   margin: auto;
//   width: 25px;
//   height: 25px;
//   display: block;
// `;

const ModalStyled = styled(Modal)`
  max-width: 400px;
  width: 100%;
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;

  & > *:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const ModalBody = styled.div`
  & > *:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  & > * {
    margin: 5px;
  }

  @media (max-width: 240px) {
    flex-wrap: wrap;
    flex-direction: column-reverse;
    justify-content: center;
  }
`;

export default LessonEditDataUnionWithBox;
